import React, { PropsWithChildren, useContext } from 'react';

import type {
  APIErrorResponse,
  Attachment,
  ErrorFromResponse,
  Message,
  MessageResponse,
  UpdatedMessage,
  UpdateMessageAPIResponse,
  UserResponse,
} from 'belo-js-sdk';

import type { StreamMessage } from './ChannelStateContext';

import type { ChannelStateReducerAction } from '../components/Channel/channelState';
import type { CustomMentionHandler } from '../components/Message/hooks/useMentionsHandler';

import type {
  ChannelUnreadUiState,
  DefaultBeloChatGenerics,
  SendMessageOptions,
  UnknownType,
  UpdateMessageOptions,
} from '../types/types';

export type MarkReadWrapperOptions = {
  /**
   * Signal, whether the `channelUnreadUiState` should be updated.
   * By default, the local state update is prevented when the Channel component is mounted.
   * This is in order to keep the UI indicating the original unread state, when the user opens a channel.
   */
  updateChannelUiUnreadState?: boolean;
};

export type MessageAttachments<
  BeloChatGenerics extends DefaultBeloChatGenerics = DefaultBeloChatGenerics
> = Array<Attachment<BeloChatGenerics>>;

export type MessageToSend<
  BeloChatGenerics extends DefaultBeloChatGenerics = DefaultBeloChatGenerics
> = {
  attachments?: MessageAttachments<BeloChatGenerics>;
  error?: ErrorFromResponse<APIErrorResponse>;
  errorStatusCode?: number;
  id?: string;
  mentioned_users?: UserResponse<BeloChatGenerics>[];
  parent?: StreamMessage<BeloChatGenerics>;
  parent_id?: string;
  status?: string;
  text?: string;
};

export type RetrySendMessage<
  BeloChatGenerics extends DefaultBeloChatGenerics = DefaultBeloChatGenerics
> = (message: StreamMessage<BeloChatGenerics>) => Promise<void>;

export type ChannelActionContextValue<
  BeloChatGenerics extends DefaultBeloChatGenerics = DefaultBeloChatGenerics
> = {
  addNotification: (text: string, type: 'success' | 'error') => void;
  closeThread: (event?: React.BaseSyntheticEvent) => void;
  deleteMessage: (
    message: StreamMessage<BeloChatGenerics>,
  ) => Promise<MessageResponse<BeloChatGenerics>>;
  dispatch: React.Dispatch<ChannelStateReducerAction<BeloChatGenerics>>;
  editMessage: (
    message: UpdatedMessage<BeloChatGenerics>,
    options?: UpdateMessageOptions,
  ) => Promise<UpdateMessageAPIResponse<BeloChatGenerics> | void>;
  jumpToFirstUnreadMessage: (queryMessageLimit?: number) => Promise<void>;
  jumpToLatestMessage: () => Promise<void>;
  jumpToMessage: (messageId: string, limit?: number) => Promise<void>;
  loadMore: (limit?: number) => Promise<number>;
  loadMoreNewer: (limit?: number) => Promise<number>;
  loadMoreThread: () => Promise<void>;
  markRead: (options?: MarkReadWrapperOptions) => void;
  onMentionsClick: CustomMentionHandler<BeloChatGenerics>;
  onMentionsHover: CustomMentionHandler<BeloChatGenerics>;
  openThread: (message: StreamMessage<BeloChatGenerics>, event?: React.BaseSyntheticEvent) => void;
  removeMessage: (message: StreamMessage<BeloChatGenerics>) => void;
  retrySendMessage: RetrySendMessage<BeloChatGenerics>;
  sendMessage: (
    message: MessageToSend<BeloChatGenerics>,
    customMessageData?: Partial<Message<BeloChatGenerics>>,
    options?: SendMessageOptions,
  ) => Promise<void>;
  setChannelUnreadUiState: React.Dispatch<React.SetStateAction<ChannelUnreadUiState | undefined>>;
  setQuotedMessage: React.Dispatch<
    React.SetStateAction<StreamMessage<BeloChatGenerics> | undefined>
  >;
  updateMessage: (message: StreamMessage<BeloChatGenerics>) => void;
};

export const ChannelActionContext = React.createContext<ChannelActionContextValue | undefined>(
  undefined,
);

export const ChannelActionProvider = <
  BeloChatGenerics extends DefaultBeloChatGenerics = DefaultBeloChatGenerics
>({
  children,
  value,
}: PropsWithChildren<{
  value: ChannelActionContextValue<BeloChatGenerics>;
}>) => (
  <ChannelActionContext.Provider value={(value as unknown) as ChannelActionContextValue}>
    {children}
  </ChannelActionContext.Provider>
);

export const useChannelActionContext = <
  BeloChatGenerics extends DefaultBeloChatGenerics = DefaultBeloChatGenerics
>(
  componentName?: string,
) => {
  const contextValue = useContext(ChannelActionContext);

  if (!contextValue) {
    console.warn(
      `The useChannelActionContext hook was called outside of the ChannelActionContext provider. Make sure this hook is called within a child of the Channel component. The errored call is located in the ${componentName} component.`,
    );

    return {} as ChannelActionContextValue<BeloChatGenerics>;
  }

  return (contextValue as unknown) as ChannelActionContextValue<BeloChatGenerics>;
};

/**
 * Typescript currently does not support partial inference, so if ChannelActionContext
 * typing is desired while using the HOC withChannelActionContext, the Props for the
 * wrapped component must be provided as the first generic.
 */
export const withChannelActionContext = <
  P extends UnknownType,
  BeloChatGenerics extends DefaultBeloChatGenerics = DefaultBeloChatGenerics
>(
  Component: React.ComponentType<P>,
) => {
  const WithChannelActionContextComponent = (
    props: Omit<P, keyof ChannelActionContextValue<BeloChatGenerics>>,
  ) => {
    const channelActionContext = useChannelActionContext<BeloChatGenerics>();

    return <Component {...(props as P)} {...channelActionContext} />;
  };

  WithChannelActionContextComponent.displayName = (
    Component.displayName ||
    Component.name ||
    'Component'
  ).replace('Base', '');

  return WithChannelActionContextComponent;
};
